// client/src/components/ProfIXChatbot.jsx
// Usage: <ProfIXChatbot
//   courseId={courseInfo?.courseId}
//   courseName={courseInfo?.title}
//   category={courseInfo?.category}   
// />
//
// Uses:
//   useChatbot  → chat messages, loading state, API calls
//   useCognitive → speakText (TTS), startRecording / stopRecording / transcribeAudio (STT)

import React, { useState, useRef, useEffect } from 'react';
import useChatbot  from '../../hooks/useChatbot';
import useCognitive from '../../hooks/useCognitive';
import MicSettingsPanel from './MicSettingsPanel';

import './profIXChatbot.css';

// ─── Lightweight markdown renderer ────────────────────────────────────────────
// Handles: **bold**, `inline code`, ```code blocks```, line breaks
const renderMarkdown = (text) => {
  if (!text) return null;

  const parts = text.split(/(```[\s\S]*?```)/g);

  return parts.map((part, i) => {
    if (part.startsWith('```')) {
      const code = part.replace(/^```[^\n]*\n?/, '').replace(/```$/, '');
      return <pre key={i} className="pix-code-block"><code>{code}</code></pre>;
    }

    const lines = part.split('\n');
    return (
      <span key={i}>
        {lines.map((line, j) => {
          const segments = line.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
          const rendered = segments.map((seg, k) => {
            if (seg.startsWith('**') && seg.endsWith('**'))
              return <strong key={k}>{seg.slice(2, -2)}</strong>;
            if (seg.startsWith('`') && seg.endsWith('`'))
              return <code key={k} className="pix-inline-code">{seg.slice(1, -1)}</code>;
            return seg;
          });
          return (
            <React.Fragment key={j}>
              {rendered}
              {j < lines.length - 1 && <br />}
            </React.Fragment>
          );
        })}
      </span>
    );
  });
};

// ─── Typing indicator ──────────────────────────────────────────────────────────
const TypingIndicator = () => (
  <div className="pix-typing">
    <div className="pix-typing-avatar">
      <img src="/images/prof_ix.png" alt="Prof-iX" />
    </div>
    <div className="pix-typing-dots">
      <span /><span /><span />
    </div>
  </div>
);

// ─── Source reference pills ────────────────────────────────────────────────────
const SourcePills = ({ sources }) => {
  if (!sources?.length) return null;
  return (
    <div className="pix-sources">
      <span className="pix-sources-label">Sources:</span>
      {sources.map((s, i) => (
        <span
          key={i}
          className={`pix-source-pill ${s.source === 'platform' ? 'pix-source-platform' : 'pix-source-ebook'}`}
        >
          {s.source === 'platform' ? '📋' : '📖'} {s.title}
        </span>
      ))}
    </div>
  );
};

// ─── Single message bubble ─────────────────────────────────────────────────────
const MessageBubble = ({ message, onSpeak, onStop, onPause, onResume, isPlaying, isPaused }) => {
  const isUser = message.role === 'user';

  return (
    <div className={`pix-message ${isUser ? 'pix-message-user' : 'pix-message-prof'}`}>
      {!isUser && (
        <div className="pix-msg-avatar">
          <img src="/images/prof_ix.png" alt="Prof-iX" />
        </div>
      )}
      <div className={`pix-bubble ${isUser ? 'pix-bubble-user' : 'pix-bubble-prof'}`}>

        {message.error ? (
          <p className="pix-bubble-error">⚠️ {message.content}</p>
        ) : (
          <div className="pix-bubble-text">{renderMarkdown(message.content)}</div>
        )}

        <div className="pix-bubble-footer">
          <span className="pix-bubble-time">
            {new Date(message.timestamp).toLocaleTimeString('en-US', {
              hour: '2-digit', minute: '2-digit', hour12: true,
            })}
          </span>

          {/* TTS controls — only on Prof-iX messages */}
          {!isUser && !message.error && (
            <div className="pix-tts-controls">

              {/* Play button — show when not playing */}
              {!isPlaying && !isPaused && (
                <button
                  className="pix-tts-btn"
                  onClick={() => onSpeak(message.id, message.content)}
                  title="Play"
                >
                  ▶️
                </button>
              )}

              {/* Pause button — show when playing */}
              {isPlaying && (
                <button
                  className="pix-tts-btn"
                  onClick={onPause}
                  title="Pause"
                >
                  ⏸️
                </button>
              )}

              {/* Resume button — show when paused */}
              {isPaused && (
                <button
                  className="pix-tts-btn pix-tts-btn--active"
                  onClick={onResume}
                  title="Resume"
                >
                  ▶️
                </button>
              )}

              {/* Stop button — show when playing or paused */}
              {(isPlaying || isPaused) && (
                <button
                  className="pix-tts-btn pix-tts-btn--stop"
                  onClick={onStop}
                  title="Stop"
                >
                  ⏹️
                </button>
              )}

            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Main component ────────────────────────────────────────────────────────────
const ProfIXChatbot = ({ userName, courseId = null, courseName = null, category = null }) => {
  // ── Hooks ──────────────────────────────────────────────────────────────────
  const {
    messages,
    isLoading,
    sendChatMessage,
    sendGatewayMessage,
    clearChat,
  } = useChatbot();

  // ─── Suggested questions shown on first open ──────────────────────────────────
  const [suggested, setSuggested] = useState(
    category === 'gateway' ? [
      'Who is Prof-iX?',
      'What makes Prof-iX unique?',
      'How does Prof-iX tutoring work?',
      'How private is my information with Prof-iX?',
    ] : courseId ? [
      'What topics does this course cover?',
      'What are the prerequisites of this course?',
      'What are the lab requirements?',
      'How is grading structured?',
    ] : [
      'What courses are available?',
      'Which course is best for beginners?',
      'What course categories are available?',
      'How does the simulator work?',
    ]
  );

  // _____________ Hooks ________________________________________________________
  const {
    isRecording,
    isPlaying,
    isPaused,
    recordingTime,
    maxRecordingTime,
    availableMics,
    selectedMicId,
    setSelectedMicId,
    loadMicrophones,
    // speakTextAzure,
    speakText,
    startRecording,
    stopRecording,
    transcribeAudio,
    stopSpeech,
    pauseSpeech,
    resumeSpeech,
    isLoading: cognitiveLoading,
  } = useCognitive();

  // ── Local UI state ──────────────────────────────────────────────────────────
  const [open, setOpen]                   = useState(false);
  const [input, setInput]                 = useState('');
  const [showSuggested, setShowSuggested] = useState(true);
  const [ttsEnabled, setTtsEnabled]       = useState(true);
  const [playingMsgId, setPlayingMsgId]   = useState(null);
  const [showMicSettings, setShowMicSettings] = useState(false);

  const bottomRef   = useRef(null);
  const textareaRef = useRef(null);
  const ttsEnabledRef = useRef(false);
  const volumeRef = useRef(1.0);
  const lastAssistantIdRef = useRef(null);

  const firstName = userName ? userName.split(' ')[0] : 'there';
  // Add near the top of the component with other constants:
  const TTS_VOICE_Azure    = 'en-US-DavisNeural';
  const TTS_LANGUAGE_Azure = 'en-US';

  const TTS_VOICE = 'onyx';   // OpenAI voices: alloy | echo | fable | onyx | nova | shimmer
  const TTS_MODEL = 'tts-1';  // tts-1 (faster) | tts-1-hd (higher quality)

  // ── Greeting on first open ─────────────────────────────────────────────────
  // We inject a fake greeting message locally — it never goes to the API
  const [greeted, setGreeted] = useState(() => {
    return localStorage.getItem('profIX_greeted') === 'true';
  });

  useEffect(() => {
    if (open && messages.length === 0) {
      if (!greeted && category !== 'gateway') {
        localStorage.setItem('profIX_greeted', 'true');
        setGreeted(true);
      }
      // Play greeting when chatbot opens
      setTimeout(() => {
        const plain = cleanForTTS(greetingMsg.content);
        setPlayingMsgId(1);
        // speakTextAzure(plain, TTS_VOICE, TTS_LANGUAGE, volumeRef.current);
        speakText(plain, TTS_VOICE, TTS_MODEL, volumeRef.current);
      }, 500);  // small delay to let the panel animate open first
    }
  }, [open]);

  const greetingMsg = {
    id:        1,
    role:      'assistant',
    content: category === 'gateway'
  ? `Hello! I'm **Prof. iX**, your guide to the Prof-iX STEM Training Platform.\n\nI can tell you about our courses, how the platform works, and help you decide if it's right for you. Feel free to ask anything!`
  : courseId
    ? greeted
      ? `Welcome back **${firstName}**! 👋\n\nAsk me anything about **${courseName || courseId}** — topics, labs, grading, or the simulator. I'm here to help!`
      : `Hello **${firstName}**! I'm **Prof. iX**, your AI course professor for **${courseName || courseId}**.\n\nAsk me anything about this course — topics, labs, grading, the simulator, or where to start. I'm here to help!`
    : greeted
      ? `Welcome back **${firstName}**! 👋\n\nAsk me about available courses, platform features, or anything IoT-related. I'm here to help!`
      : `Hello **${firstName}**! I'm **Prof. iX**, your AI assistant for the Prof-iX STEM Training Platform.\n\nAsk me about available courses, platform features, or anything IoT-related. I'm here to help!`,
    sources:   [],
    timestamp: new Date(),
  };

  // ── Auto-scroll ────────────────────────────────────────────────────────────
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // ── Focus textarea when opened ─────────────────────────────────────────────
  useEffect(() => {
    if (open) setTimeout(() => textareaRef.current?.focus(), 300);
  }, [open]);

  // ── Auto-speak Prof-iX responses when TTS is enabled ─────────────────────
  useEffect(() => {
    if (!ttsEnabledRef.current || messages.length === 0) return;
    const last = messages[messages.length - 1];
    if (last.role === 'assistant' && !last.error) {
      const plain = cleanForTTS(last.content);
      setPlayingMsgId(last.id);   // ← track which message is playing
      // speakTextAzure(plain, TTS_VOICE, TTS_LANGUAGE, volumeRef.current);
      speakText(plain, TTS_VOICE, TTS_MODEL, volumeRef.current);
    }
  }, [messages]);

  useEffect(() => {
    ttsEnabledRef.current = ttsEnabled;
  }, [ttsEnabled]);

  useEffect(() => {
    if (!isPlaying && !isPaused) {
      setPlayingMsgId(null);
    }
  }, [isPlaying, isPaused]);

  // ── Auto-resize textarea ───────────────────────────────────────────────────
  const handleInputChange = (e) => {
    setInput(e.target.value);
    const el = textareaRef.current;
    if (el) {
      el.style.height = 'auto';
      el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
    }
  };

  // ── Handle send ────────────────────────────────────────────────────────────
  const handleSend = async (text) => {
    const trimmed = (text || input).trim();
    if (!trimmed || isLoading) return;

    // Remove clicked suggestion from list
    if (text) {
      setSuggested(prev => prev.filter(q => q !== text));
    } else {
      setShowSuggested(false);
    }

    setInput('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';

    if (category === 'gateway') {
      await sendGatewayMessage(trimmed);
    } else {
      await sendChatMessage(
        courseId   || 'general',
        courseName || 'Prof-iX Platform',
        category   || 'general',
        trimmed
      );
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // ── STT mic button ─────────────────────────────────────────────────────────
  // const handleMicClick = async () => {
  //   if (isRecording) {
  //     await stopRecording();               // waits until blob is ready
  //     const result = await transcribeAudio();
  //     if (result?.success && result.text) {
  //       setInput(prev => prev + (prev ? ' ' : '') + result.text);
  //       textareaRef.current?.focus();
  //     }
  //   } else {
  //     await startRecording();
  //   }
  // };
  const handleMicClick = async () => {
    if (isRecording) {
      await stopRecording();
      const result = await transcribeAudio();
      if (result?.success && result.text) {
        setInput(prev => prev + (prev ? ' ' : '') + result.text);
        textareaRef.current?.focus();
      }
    } else {
      await startRecording();
    }
  };
  // ── TTS toggle ─────────────────────────────────────────────────────────────
  const handleTtsToggle = () => {
    if (ttsEnabled) stopSpeech();
    setTtsEnabled(prev => !prev);
  };
  
  const cleanForTTS = (text) => {
  return text
    .replace(/Prof-iX/gi,  'Profix')  
    .replace(/Prof\. iX/gi, 'Profix') 
    .replace(/Prof\. ix/gi, 'Profix')

    // Markdown formatting
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/```[\s\S]*?```/g, 'code block')
    .replace(/#{1,6}\s/g, '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')

    // Speak symbols as words
    .replace(/%/g,  ' percent ')
    .replace(/@/g,  ' at ')
    .replace(/&/g,  ' and ')
    .replace(/\+/g, ' plus ')
    .replace(/=/g,  ' equals ')
    .replace(/\//g, ' slash ')
    .replace(/\\/g, ' backslash ')
    .replace(/\|/g, ' or ')
    .replace(/>/g,  ' greater than ')
    .replace(/</g,  ' less than ')
    .replace(/\^/g, ' to the power of ')
    .replace(/~/g,  ' approximately ')
    .replace(/#/g,  ' number ')
    .replace(/\$/g, ' dollars ')
    .replace(/kΩ/g, ' kilo ohms ')
    .replace(/Ω/g,  ' ohms ')
    .replace(/μ/g,  ' micro ')
    .replace(/°C/g, ' degrees celsius ')
    .replace(/°F/g, ' degrees fahrenheit ')
    .replace(/°/g,  ' degrees ')
    .replace(/²/g,  ' squared ')
    .replace(/³/g,  ' cubed ')

    // Remove emojis and other unicode symbols
    .replace(/[\u{1F300}-\u{1F9FF}]/gu, '')  // removes all emojis including 👋 🎓 ✅ etc.
    .replace(/[\u2600-\u26FF]/gu, '')         // removes misc symbols ☀ ⚡ etc.
    .replace(/[\u2700-\u27BF]/gu, '')         // removes dingbats ✂ ✈ etc.

    // Clean up remaining symbols
    .replace(/[!*_~`>|\\]/g, '')
    .replace(/\.{2,}/g, '.')
    .replace(/\s{2,}/g, ' ')
    .trim();
  };
  // ── Speak a specific message on demand ────────────────────────────────────
  const handleSpeak = (msgId, text) => {
    const plain = cleanForTTS(text);
    setPlayingMsgId(msgId);
    // speakTextAzure(plain, TTS_VOICE, TTS_LANGUAGE, volumeRef.current);
    speakText(plain, TTS_VOICE, TTS_MODEL, volumeRef.current);
  };

  const handleStop = () => {
    stopSpeech();
    setPlayingMsgId(null);
  };

  // ── Clear chat ─────────────────────────────────────────────────────────────
  const handleClearChat = () => {
    stopSpeech();
    clearChat();
    setShowSuggested(true);
    setGreeted(false);
  };

  // ── All messages to display (greeting is local, not from hook) ────────────
  const displayMessages = messages.length === 0 ? [greetingMsg] : [greetingMsg, ...messages];

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      {/* ── Floating Trigger Button ── */}
      <button
        className={`pix-fab ${open ? 'pix-fab-open' : ''}`}
        onClick={() => { if (open) { stopSpeech(); setPlayingMsgId(null); } setOpen(o => !o); }}
        title="Chat with Prof-iX"
      >
        {open ? (
          <span className="pix-fab-close">✕</span>
        ) : (
          <>
            <img src="/images/prof_ix.png" alt="Prof-iX" className="pix-fab-img" />
            <span className="pix-fab-pulse" />
          </>
        )}
      </button>

      {/* ── Chat Panel ── */}
      {open && (
        <div className="pix-panel">

          {/* Header */}
          <div className="pix-header">
            <div className="pix-header-grid" />
            <div className="pix-header-content">
              <div className="pix-header-avatar">
                <img src="/images/prof_ix.png" alt="Prof-iX" />
                <span className="pix-online-dot" />
              </div>
              <div className="pix-header-info">
                <h4>Prof-iX</h4>
                <span>AI Course Professor · {courseId}</span>
              </div>
              <div className="pix-header-actions">
                {/* TTS auto-speak toggle */}
                <button
                  className={`pix-icon-btn ${ttsEnabled ? 'pix-icon-btn-active' : ''}`}
                  onClick={handleTtsToggle}
                  title={ttsEnabled ? 'Auto-speak ON — click to turn off' : 'Auto-speak OFF'}
                >
                  {ttsEnabled ? '🔊' : '🔇'}
                </button>
                <button
                  className="pix-mic-settings-btn"
                  onClick={() => {
                    loadMicrophones();
                    setShowMicSettings(prev => !prev);
                  }}
                  title="Microphone settings"
                  type="button"
                >
                  ⚙️
                </button>
                <button className="pix-icon-btn" onClick={handleClearChat} title="Clear chat">
                  🗑️
                </button>
                <button className="pix-icon-btn" onClick={() => { stopSpeech(); setPlayingMsgId(null); setOpen(false); }} title="Close">
                  ✕
                </button>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="pix-messages">

            {displayMessages.map(msg => (
              <MessageBubble
                key={msg.id}
                message={msg}
                onSpeak={handleSpeak}
                onStop={handleStop}
                onPause={pauseSpeech}
                onResume={resumeSpeech}
                isPlaying={isPlaying && playingMsgId === msg.id}
                isPaused={isPaused && playingMsgId === msg.id}
              />
            ))}

            {isLoading && <TypingIndicator />}

            {/* Suggested questions — shown only when no real messages yet */}
            {showSuggested && suggested.length > 0 && (
              <div className="pix-suggested">
                <p className="pix-suggested-label">Suggested questions</p>
                <div className="pix-suggested-grid">
                  {suggested.map((q, i) => (
                    <button
                      key={i}
                      className="pix-suggested-btn"
                      onClick={() => handleSend(q)}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div ref={bottomRef} />
          </div>

          {/* Input area */}
          <div className="pix-input-area">
            <div className="pix-input-row">

              {/* Mic button */}
              {/* <button
                className={`pix-mic-btn ${isRecording ? 'pix-mic-recording' : ''}`}
                onClick={handleMicClick}
                title={isRecording ? 'Stop recording' : 'Speak your question'}
                type="button"
              >
                {isRecording ? '⏹' : '🎤'}
              </button> */}

              {/* Mic area — idle or recording */}
              <div className="pix-mic-area">
                {isRecording ? (
                  <div className="pix-recording-indicator" onClick={handleMicClick} title="Click to stop">
                    <span className="pix-rec-circle" />
                    <span className="pix-rec-countdown">{recordingTime}s</span>
                    <span className="pix-rec-stop-label">tap to stop</span>
                  </div>
                ) : (
                  <>
                    <button
                      className="pix-mic-btn"
                      onClick={handleMicClick}
                      title="Speak your question"
                      type="button"
                    >
                      🎤
                    </button>
                  </>
                )}

                {/* Mic settings dropdown */}
                {showMicSettings && !isRecording && (
                  <MicSettingsPanel
                    availableMics={availableMics}
                    selectedMicId={selectedMicId}
                    onSelectMic={setSelectedMicId}
                    onClose={() => setShowMicSettings(false)}
                    startRecording={startRecording}
                    stopRecording={stopRecording}
                    transcribeAudio={transcribeAudio}
                    isLoading={cognitiveLoading}
                  />
                )}
              </div>

              <textarea
                ref={textareaRef}
                className="pix-input"
                value={input}
                onChange={handleInputChange}
                onKeyDown={handleKey}
                placeholder={
                  isRecording
                    ? `Recording... ${recordingTime}s remaining`
                    : `Ask Prof-iX about ${courseName || courseId}…`
                }
                rows={1}
                disabled={isRecording}
              />

              <button
                className="pix-send-btn"
                onClick={() => handleSend()}
                disabled={!input.trim() || isLoading || isRecording}
                type="button"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
                  strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
              </button>
            </div>
            <p className="pix-disclaimer">Prof-iX uses AI · Verify important info</p>
          </div>

        </div>
      )}
    </>
  );
};

export default ProfIXChatbot;
